import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Phone, Mail, MapPin, Clock, Send, MessageCircle, Loader2, ArrowRight, CheckCircle2 } from 'lucide-react';
import { companyInfo, courses } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    course: '',
    message: '',
    trainingFormat: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Inquiry sent successfully! We'll get back to you within 24 hours.");
      setFormData({ name: '', email: '', phone: '', company: '', course: '', message: '', trainingFormat: '' });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-white to-orange-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-20 w-64 h-64 border-2 border-orange-300 rounded-full"></div>
        <div className="absolute bottom-40 left-10 w-80 h-80 border-2 border-yellow-300 rounded-full"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Header - Centered with Floating Elements */}
        <div className="text-center mb-16 relative">
          <div className="inline-flex items-center space-x-2 bg-orange-100 px-6 py-3 rounded-full mb-6">
            <MessageCircle className="h-5 w-5 text-orange-600" />
            <span className="text-orange-700 font-semibold">Get In Touch</span>
          </div>
          <h2 className="text-5xl font-black text-gray-900 mb-6 leading-tight">
            Start Your Training <br />
            <span className="bg-gradient-to-r from-orange-600 to-yellow-600 bg-clip-text text-transparent">
              Journey Today
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Ready to enhance your team's safety awareness? Contact our training experts to discuss your needs 
            and find the perfect well control certification program for your organization.
          </p>
        </div>

        {/* Main Content - Asymmetric Layout */}
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Contact Form - Takes 8 columns */}
          <div className="lg:col-span-8">
            <Card className="border-0 shadow-2xl hover:shadow-3xl transition-all duration-300 overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-orange-500 to-yellow-500"></div>
              <CardHeader className="bg-gradient-to-br from-slate-900 to-orange-900 text-white p-8">
                <CardTitle className="text-3xl font-black flex items-center">
                  <Send className="h-8 w-8 mr-3 text-orange-300" />
                  Send us a Message
                </CardTitle>
                <p className="text-orange-100 text-lg">Fill out the form below and we'll get back to you within 24 hours</p>
              </CardHeader>
              <CardContent className="p-8 bg-white">
                {submitted ? (
                  <div data-testid="contact-success-message" className="flex flex-col items-center justify-center text-center py-16 space-y-4">
                    <CheckCircle2 className="h-16 w-16 text-green-500" />
                    <h3 className="text-2xl font-black text-gray-900">Inquiry Sent Successfully!</h3>
                    <p className="text-gray-600 max-w-md">Thank you for reaching out. Our training experts will get back to you within 24 hours. A confirmation has been sent to your email.</p>
                    <Button
                      data-testid="contact-send-another-button"
                      onClick={() => setSubmitted(false)}
                      variant="outline"
                      className="mt-4 border-2 border-orange-500 text-orange-600 hover:bg-orange-50 rounded-xl"
                    >
                      Send Another Inquiry
                    </Button>
                  </div>
                ) : (
                <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
                  {/* Personal Information */}
                  <div className="space-y-6">
                    <h3 className="text-xl font-bold text-gray-900 border-b border-orange-200 pb-2">Personal Information</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <Label htmlFor="name" className="text-gray-700 font-semibold">Full Name *</Label>
                        <Input 
                          id="name"
                          data-testid="contact-name-input"
                          placeholder="Enter your full name"
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          required
                          className="border-2 border-gray-200 focus:border-orange-500 rounded-xl p-4 text-lg"
                        />
                      </div>
                      <div className="space-y-3">
                        <Label htmlFor="email" className="text-gray-700 font-semibold">Email Address *</Label>
                        <Input 
                          id="email"
                          data-testid="contact-email-input"
                          type="email"
                          placeholder="Enter your email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          required
                          className="border-2 border-gray-200 focus:border-orange-500 rounded-xl p-4 text-lg"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <Label htmlFor="phone" className="text-gray-700 font-semibold">Phone Number</Label>
                        <Input 
                          id="phone"
                          data-testid="contact-phone-input"
                          placeholder="Enter your phone number"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          className="border-2 border-gray-200 focus:border-orange-500 rounded-xl p-4 text-lg"
                        />
                      </div>
                      <div className="space-y-3">
                        <Label htmlFor="company" className="text-gray-700 font-semibold">Company/Organization</Label>
                        <Input 
                          id="company"
                          data-testid="contact-company-input"
                          placeholder="Enter your company name"
                          value={formData.company}
                          onChange={(e) => handleInputChange('company', e.target.value)}
                          className="border-2 border-gray-200 focus:border-orange-500 rounded-xl p-4 text-lg"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Training Preferences */}
                  <div className="space-y-6">
                    <h3 className="text-xl font-bold text-gray-900 border-b border-orange-200 pb-2">Training Preferences</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <Label htmlFor="course" className="text-gray-700 font-semibold">Interested Course</Label>
                        <Select value={formData.course} onValueChange={(value) => handleInputChange('course', value)}>
                          <SelectTrigger className="border-2 border-gray-200 focus:border-orange-500 rounded-xl p-4 text-lg">
                            <SelectValue placeholder="Select a course" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General Inquiry</SelectItem>
                            {courses.map((course) => (
                              <SelectItem key={course.id} value={course.title}>
                                {course.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-3">
                        <Label htmlFor="trainingFormat" className="text-gray-700 font-semibold">Preferred Format</Label>
                        <Select value={formData.trainingFormat} onValueChange={(value) => handleInputChange('trainingFormat', value)}>
                          <SelectTrigger className="border-2 border-gray-200 focus:border-orange-500 rounded-xl p-4 text-lg">
                            <SelectValue placeholder="Select training format" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="classroom">Classroom (Gujarat)</SelectItem>
                            <SelectItem value="virtual">Virtual Instructor Led</SelectItem>
                            <SelectItem value="onsite">On-site Corporate Training</SelectItem>
                            <SelectItem value="flexible">Flexible - Discuss Options</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-3">
                    <Label htmlFor="message" className="text-gray-700 font-semibold">Tell us about your requirements</Label>
                    <Textarea 
                      id="message"
                      data-testid="contact-message-input"
                      placeholder="Share details about your training needs, team size, preferred dates, specific requirements, or any questions you have..."
                      rows={5}
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      className="border-2 border-gray-200 focus:border-orange-500 rounded-xl p-4 text-lg"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    size="lg"
                    disabled={submitting}
                    data-testid="contact-submit-button"
                    className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white text-xl py-6 rounded-xl font-bold shadow-xl hover:shadow-2xl transition-all hover:scale-105 disabled:opacity-70 disabled:hover:scale-100"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-6 w-6 mr-3 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-6 w-6 mr-3" />
                        Send Training Inquiry
                        <ArrowRight className="h-6 w-6 ml-3" />
                      </>
                    )}
                  </Button>
                </form>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Contact Information Sidebar - Takes 4 columns */}
          <div className="lg:col-span-4 space-y-8">
            {/* Contact Details Card */}
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-blue-500 to-purple-500"></div>
              <CardHeader className="bg-gradient-to-br from-blue-600 to-purple-700 text-white p-6">
                <CardTitle className="text-2xl font-bold">Contact Information</CardTitle>
                <p className="text-blue-100">Get in touch with our training experts</p>
              </CardHeader>
              <CardContent className="p-6 bg-white space-y-6">
                <div className="space-y-4">
                  <div className="flex items-start space-x-4 p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-lg">Email</p>
                      <a href={`mailto:${companyInfo.email}`} className="text-blue-600 hover:text-blue-700 font-medium text-lg">
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-lg">Phone</p>
                      <a href={`tel:${companyInfo.phone}`} className="text-green-600 hover:text-green-700 font-medium text-lg">
                        {companyInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 p-4 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-lg">Location</p>
                      <p className="text-orange-600 font-medium text-lg">{companyInfo.location}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-lg">Response Time</p>
                      <p className="text-purple-600 font-medium text-lg">Within 24 hours</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats Card */}
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-orange-500 to-yellow-500"></div>
              <CardContent className="p-6 bg-gradient-to-br from-orange-50 to-yellow-50">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Training Options Available</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3 p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                    <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                    <span className="text-gray-700 font-medium">Classroom training in Gujarat</span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-700 font-medium">Virtual Instructor Led Training</span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-gray-700 font-medium">On-site corporate training</span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                    <span className="text-gray-700 font-medium">Customized group programs</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Action Card */}
            <Card className="border-0 shadow-xl overflow-hidden">
              <CardContent className="p-6 bg-gradient-to-br from-slate-900 to-orange-900 text-white text-center">
                <h3 className="text-2xl font-bold mb-4">Need Immediate Assistance?</h3>
                <p className="text-orange-100 mb-6">Call our training experts directly for immediate consultation and course recommendations.</p>
                <a 
                  href={`tel:${companyInfo.phone}`}
                  className="inline-flex items-center justify-center bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white px-6 py-3 rounded-xl font-bold transition-all hover:scale-105 shadow-lg"
                >
                  <Phone className="h-5 w-5 mr-2" />
                  Call Now
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};